import { useContext, useEffect, useState } from 'react'
import { Coordinates, Position } from 'types'
import { ArtboardContext } from '../../board/context'

export const getRotatedCoordinates = ({ x, y }: Coordinates, center: Coordinates, rotation: number): Coordinates => {
    const radians = (Math.PI / 180) * rotation
    const cos = Math.cos(radians)
    const sin = Math.sin(radians)

    return {
        x: cos * (x - center.x) + sin * (y - center.y) + center.x,
        y: cos * (y - center.y) - sin * (x - center.x) + center.y,
    }
}

const useClipPath = (position: Position, disabled: boolean = false): string | undefined => {
    const { height, rotation = 0, width, x, y } = position
    const board = useContext(ArtboardContext)
    const [path, setPath] = useState<string>()

    useEffect(() => {
        if (disabled) {
            setPath(undefined)
            return
        }

        const mid = { x: width / 2, y: height / 2 }
        const corners = [
            { x: x - mid.x, y: y - mid.y },
            { x: x + mid.x, y: y - mid.y },
            { x: x + mid.x, y: y + mid.y },
            { x: x - mid.x, y: y + mid.y },
        ]
        const rotatedCorners = corners.map((corner) => getRotatedCoordinates(corner, { x, y }, rotation))
        const xs = rotatedCorners.map((corner) => corner.x).sort((a, b) => a - b)
        const ys = rotatedCorners.map((corner) => corner.y).sort((a, b) => a - b)
        const bounds: Position = board.crop || {
            x: 0,
            y: 0,
            width: board.width,
            height: board.height,
        }

        if (
            xs[0] < bounds.x ||
            ys[0] < bounds.y ||
            xs[xs.length - 1] > bounds.x + bounds.width ||
            ys[ys.length - 1] > bounds.y + bounds.height
        ) {
            const x = Math.round(-corners[0].x) + bounds.x
            const y = Math.round(-corners[0].y) + bounds.y
            const mask = [
                { x, y },
                { x: x + bounds.width, y },
                { x: x + bounds.width, y: y + bounds.height },
                { x, y: y + bounds.height },
            ].map((maskCorner) => getRotatedCoordinates(maskCorner, mid, rotation))

            setPath(`polygon(${mask.map(({ x, y }) => `${x}px ${y}px`).join(',')})`)
        } else {
            setPath(undefined)
        }
    }, [board.crop, board.height, board.width, disabled, height, rotation, width, x, y])

    return path
}

export default useClipPath
